// İSG uzmanı portalı — Taşeron detayı: Uygunluk belgeleri sekmesi (api-contract §13.2).
// `GET /contractors/{uuid}/documents` liste (tür, dosya, geçerlilik, **durum rozeti**
// Valid/Expiring/Expired/Missing) + belge ekle (§8 yükleme → link) + indir (§8 presigned
// GET) + sil. Uygunluk özeti belgelerden türetilir → değişimde taşeron detayı tazelenir.
// Tür etiketi §27 kaydından (`label('company', type)`; GL #7) ve kırpılmaz — uzun (Almanca) etiket
// satır kaydırır. Firma belgelerinde onay/red yoktur, bu yüzden yapay zekâ tanıma işareti de yok.

import {
  Button,
  ConfirmDialog,
  type ContractorDocumentDto,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  Tag,
  contractorsApi,
  currentCulture,
  documentsApi,
  resolveApiErrorMessage,
  useDocumentTypes,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { formatDate } from '../../lib/format';
import { TabPanelIntro } from '../TabPanelIntro';
import { DocumentLinkModal } from './DocumentLinkModal';
import { complianceTone } from './tone';

interface DocumentsPanelProps {
  contractorUuid: string;
}

/** Geçerlilik aralığı görüntü metni (DateOnly, §4); ikisi de yoksa tire. */
function validityRange(document: ContractorDocumentDto, culture: string): string {
  const from = document.validFrom ? formatDate(document.validFrom, culture) : null;
  const until = document.validUntil ? formatDate(document.validUntil, culture) : null;
  if (from && until) {
    return `${from} – ${until}`;
  }
  return until ?? from ?? '—';
}

export function DocumentsPanel({ contractorUuid }: DocumentsPanelProps) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const queryClient = useQueryClient();
  const culture = currentCulture();

  const [addOpen, setAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState<ContractorDocumentDto | null>(null);

  const query = useQuery({
    queryKey: contractorsKeys.documents(contractorUuid),
    queryFn: () => contractorsApi.listContractorDocuments(contractorUuid),
  });

  const downloadMutation = useMutation({
    mutationFn: (documentUuid: string) => documentsApi.getDocument(documentUuid),
    onSuccess: (download) => {
      // Presigned GET — özgün dosya adıyla iner (content-disposition attachment).
      window.open(download.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });

  // DELETE hedefi §13.2'de **link kaydının kendi `uuid`'idir** (DTO `uuid` ← entity.Uuid),
  // §8 storage `documentUuid`'i DEĞİL (backend `d => d.Uuid == documentUuid` ile çözer;
  // ContractorDocumentWorkflow.DeleteAsync). İkisi ayrı GUID; §8 uuid gönderilirse her silme
  // `404 CONTRACTOR_DOCUMENT_NOT_FOUND` döner. (İndirme aksiyonu §8 `documentUuid` kullanır — doğru.)
  const deleteMutation = useMutation({
    mutationFn: (linkUuid: string) =>
      contractorsApi.deleteContractorDocument(contractorUuid, linkUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractorsKeys.documents(contractorUuid) });
      queryClient.invalidateQueries({ queryKey: contractorsKeys.detail(contractorUuid) });
      queryClient.invalidateQueries({ queryKey: contractorsKeys.list() });
      setToDelete(null);
    },
  });

  const rows = query.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <TabPanelIntro
        action={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('contractors.documents.add.new')}
          </Button>
        }
      >
        {t('contractors.documents.subtitle')}
      </TabPanelIntro>

      <Table caption={t('contractors.documents.title')}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('contractors.documents.col.type')}</TableHead>
            <TableHead>{t('contractors.documents.col.file')}</TableHead>
            <TableHead>{t('contractors.documents.col.validity')}</TableHead>
            <TableHead>{t('contractors.documents.col.status')}</TableHead>
            <TableHead className="text-right">{t('contractors.common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isPending ? (
            <TableMessageRow colSpan={5}>
              <Spinner label={t('contractors.common.loading')} />
            </TableMessageRow>
          ) : query.isError ? (
            <TableMessageRow colSpan={5}>{resolveApiErrorMessage(query.error, t)}</TableMessageRow>
          ) : rows.length === 0 ? (
            <TableMessageRow colSpan={5}>
              <EmptyState icon={FileText} title={t('contractors.documents.empty')} />
            </TableMessageRow>
          ) : (
            rows.map((document) => (
              <TableRow key={document.uuid}>
                <TableCell
                  truncate={false}
                  className="break-words font-medium text-fg carbon:h-auto carbon:py-2"
                >
                  {label('company', document.type)}
                </TableCell>
                <TableCell truncate={false} className="text-muted">
                  <div className="flex min-w-0 flex-col">
                    <span className="text-fg [overflow-wrap:anywhere]">
                      {document.fileName ?? '—'}
                    </span>
                    {document.note ? (
                      <span className="text-xs text-muted">{document.note}</span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted">
                  {validityRange(document, culture)}
                </TableCell>
                <TableCell>
                  <Tag color={complianceTone(document.validityStatus)}>
                    {t(`contractors.compliance.${document.validityStatus.toLowerCase()}`)}
                  </Tag>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadMutation.mutate(document.documentUuid)}
                      loading={
                        downloadMutation.isPending &&
                        downloadMutation.variables === document.documentUuid
                      }
                      aria-label={t('contractors.documents.action.download')}
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setToDelete(document)}
                      aria-label={t('contractors.documents.action.delete')}
                    >
                      <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <DocumentLinkModal open={addOpen} onOpenChange={setAddOpen} contractorUuid={contractorUuid} />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title={t('contractors.documents.delete.title')}
        body={t('contractors.documents.delete.body', {
          type: toDelete ? label('company', toDelete.type) : '',
        })}
        confirmLabel={t('contractors.documents.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
